/**
 * Auth store — manages user authentication state.
 *
 * Uses Zustand for lightweight state management.
 * Stores tokens in localStorage via api.ts token helpers.
 */

import { create } from 'zustand';

import { api, setAccessToken, getAccessToken, getRefreshToken, setRefreshToken, setOnLogout } from './api.js';
import { currentLocale, localizeErrorCode } from './error-localization.js';

import type { PendingEmailVerificationResponse } from './api.js';
import type { AuthTokens } from '@dentai/shared';

export interface AuthUser {
  userId: number;
  email: string;
  name: string | null;
  role: string;
  permissions: string[];
  organizationId: number | null;
  clinicId: number | null;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  verificationRequiredEmail: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (input: {
    email: string;
    password: string;
    name: string;
    organizationName?: string;
    clinicName?: string;
    timezone?: string;
    plan?: 'free' | 'trial';
  }) => Promise<PendingEmailVerificationResponse>;
  logout: () => Promise<void>;
  clearError: () => void;
  initialize: () => void;
}

function storeTokens(tokens: AuthTokens): void {
  setAccessToken(tokens.accessToken);
  setRefreshToken(tokens.refreshToken);
}

function clearTokens(): void {
  setAccessToken(null);
  setRefreshToken(null);
}

// Decode JWT payload (without verification — just to get user info)
function decodeJwt(token: string): { sub: number; email?: string; name?: string; role?: string; permissions?: string[]; org_id?: number; clinic_id?: number } | null {
  try {
    const payload = token.split('.')[1] ?? '';
    const decoded: unknown = JSON.parse(atob(payload));
    if (typeof decoded !== 'object' || decoded === null || !('sub' in decoded) || typeof decoded.sub !== 'number') return null;
    const result: { sub: number; email?: string; name?: string; role?: string; permissions?: string[]; org_id?: number; clinic_id?: number } = { sub: decoded.sub };
    if ('email' in decoded && typeof decoded.email === 'string') result.email = decoded.email;
    if ('name' in decoded && typeof decoded.name === 'string') result.name = decoded.name;
    if ('role' in decoded && typeof decoded.role === 'string') result.role = decoded.role;
    if ('permissions' in decoded && Array.isArray(decoded.permissions) && decoded.permissions.every((permission) => typeof permission === 'string')) result.permissions = decoded.permissions;
    if ('org_id' in decoded && typeof decoded.org_id === 'number') result.org_id = decoded.org_id;
    if ('clinic_id' in decoded && typeof decoded.clinic_id === 'number') result.clinic_id = decoded.clinic_id;
    return result;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  verificationRequiredEmail: null,

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.auth.login(email, password);
      if (res.error) {
        if (res.error.code === 'EMAIL_VERIFICATION_REQUIRED') {
          set({ verificationRequiredEmail: email });
          throw new Error(localizeErrorCode('EMAIL_VERIFICATION_REQUIRED', currentLocale()));
        }
        throw new Error(res.error.message);
      }
      if (!res.data) {
        throw new Error(localizeErrorCode('INTERNAL_ERROR', currentLocale()));
      }
      storeTokens(res.data.tokens);
      set({
        user: { userId: res.data.user.id, email: res.data.user.email, name: res.data.user.name ?? null, role: res.data.user.role, permissions: decodeJwt(res.data.tokens.accessToken)?.permissions ?? [], organizationId: res.data.user.organizationId, clinicId: res.data.user.clinicId },
        isAuthenticated: true,
        isLoading: false,
        error: null,
        verificationRequiredEmail: null,
      });
    } catch (err) {
      clearTokens();
      set({ isLoading: false, error: (err as Error).message, isAuthenticated: false, user: null });
      throw err;
    }
  },

  register: async (input) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.auth.register(input);
      if (res.error) {
        throw new Error(res.error.message);
      }
      if (!res.data) {
        throw new Error(localizeErrorCode('INTERNAL_ERROR', currentLocale()));
      }
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        verificationRequiredEmail: res.data.email,
      });
      return res.data;
    } catch (err) {
      clearTokens();
      set({ isLoading: false, error: (err as Error).message, isAuthenticated: false, user: null });
      throw err;
    }
  },

  logout: async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        await api.auth.logout(refreshToken);
      } catch {
        // Ignore errors — we're logging out anyway
      }
    }
    clearTokens();
    set({ user: null, isAuthenticated: false, error: null, isLoading: false, verificationRequiredEmail: null });
  },

  clearError: () => set({ error: null }),

  initialize: () => {
    const token = getAccessToken();
    if (token) {
      const payload = decodeJwt(token);
      if (payload) {
        set({
          user: { userId: payload.sub, email: payload.email ?? '', name: payload.name ?? null, role: payload.role ?? 'unknown', permissions: payload.permissions ?? [], organizationId: payload.org_id ?? null, clinicId: payload.clinic_id ?? null },
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        clearTokens();
        set({ user: null, isAuthenticated: false, isLoading: false });
      }
    } else {
      set({ isLoading: false });
    }
  },
}));

// Register a callback so the API client can trigger logout on session expiry
setOnLogout(() => {
  void useAuthStore.getState().logout();
  window.location.hash = '/login';
});
